import { getDangerousWasteRecordValue } from './dangerousWasteRecordColumns.js';

// Admin surfaces label records from every kind of unit: an inference record's `eval` is a
// plain class-name string, while a dangerous-waste record's `eval` is a map — keyed by column
// id on older records, and on newer ones a positional `values` list alongside the `columns`
// that were in effect when it was evaluated. Rendering that map directly puts an object where
// a string belongs, so every admin reader goes through here.
//
// There are two labels, because the surfaces want different things. A card showing one scan
// wants to name the thing that was scanned; a chart bucketing a week of scans wants a column
// with few enough distinct values to make rows worth reading.
const KEYED_EVAL_LABEL_FIELDS = ['category', 'class_name', 'label'];

function firstPositionalValue(values) {
    if (!Array.isArray(values) || values.length === 0) return null;

    const [firstValue] = values;
    if (typeof firstValue === 'string') return firstValue.trim() || null;
    if (typeof firstValue === 'number' || typeof firstValue === 'boolean') return String(firstValue);

    return null;
}

function keyedEvalLabel(evalValue, fields) {
    const field = fields.find(
        (name) => typeof evalValue[name] === 'string' && evalValue[name].trim().length > 0,
    );

    return field ? evalValue[field].trim() : null;
}

// Resolves one of the receiver's columns against whichever shape this eval has: matched by id
// through its eval-time columns list, or read straight off a legacy eval's key.
function evalColumnValue(evalValue, columnId) {
    return getDangerousWasteRecordValue({ eval: evalValue }, columnId) || null;
}

// For a surface naming a single scan. The constituent is what identifies it to an admin, so it
// wins wherever the record carries one. A positional eval whose columns list is missing (or
// which never named a constituent) still puts its most identifying value first, so that is the
// fallback. Only then come the keyed fields an inference record uses.
export function getAdminEvalDisplayLabel(evalValue) {
    if (typeof evalValue === 'string') return evalValue.trim() || null;
    if (!evalValue || typeof evalValue !== 'object') return null;

    return evalColumnValue(evalValue, 'constituent')
        || firstPositionalValue(evalValue.values)
        || keyedEvalLabel(evalValue, KEYED_EVAL_LABEL_FIELDS);
}

// For a chart bucketing many scans. The category is a small fixed set per receiver, where the
// constituent is free text that would grow a row per distinct chemical name, so grouping stays
// on the category and never falls back to the constituent. A record with no category at all is
// left unlabelled for the caller to bucket as unknown, exactly as it was before the positional
// eval format existed — the difference now is that a positional eval naming a category in its
// columns list resolves instead of landing there.
export function getAdminEvalGroupLabel(evalValue) {
    if (typeof evalValue === 'string') return evalValue.trim() || null;
    if (!evalValue || typeof evalValue !== 'object') return null;

    return evalColumnValue(evalValue, 'category')
        || keyedEvalLabel(evalValue, ['class_name', 'label']);
}
